import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import MailIcon from '@mui/icons-material/Mail';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { useNavigate } from 'react-router-dom';

import { encryptKey } from '../../utilities/common-encrypt-util';

const WEB_API_URL = process.env.REACT_APP_WEB_API_URL;
export const MESSAGE_STATUS_CHANGED_EVENT = 'auditVaultMessageStatusChanged';

export default function HeaderMessageIndicator({ user }) {
    const navigate = useNavigate();
    const [unreadCount, setUnreadCount] = useState(0);
    const companyId = Number(user?.companyId || 0);

    const fetchUnreadCountFromMessages = useCallback(async () => {
        const key = await encryptKey(JSON.stringify({
            companyId,
            returnDeleted: false,
            userMicrosoftId: user.microsoftUserId,
        }));
        const response = await axios.get(
            `${WEB_API_URL}MessageCenter/GetAllMessagesForCompany`,
            { params: { key } }
        );
        const messages = Array.isArray(response.data) ? response.data : [];
        return messages.filter((message) => message.shadowIsRead === false).length;
    }, [companyId, user?.microsoftUserId]);

    const fetchUnreadCount = useCallback(async () => {
        if (companyId <= 0 || !user?.microsoftUserId) {
            setUnreadCount(0);
            return;
        }

        try {
            const key = await encryptKey(JSON.stringify({
                companyId,
                userMicrosoftId: user.microsoftUserId,
            }));
            const response = await axios.get(
                `${WEB_API_URL}MessageCenter/GetUnreadMessageCountForCompany`,
                { params: { key } }
            );
            setUnreadCount(Number(response.data) || 0);
        } catch (error) {
            // The message-list API is also used by the Support Home preview. Keep
            // the header working during rolling deployments where the newer count
            // endpoint may not yet be available on the running API instance.
            try {
                setUnreadCount(await fetchUnreadCountFromMessages());
            } catch (fallbackError) {
                console.log('HeaderMessageIndicator.fetchUnreadCount ERROR:', error);
                console.log('HeaderMessageIndicator.fetchUnreadCount fallback ERROR:', fallbackError);
            }
        }
    }, [companyId, fetchUnreadCountFromMessages, user?.microsoftUserId]);

    useEffect(() => {
        fetchUnreadCount();

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                fetchUnreadCount();
            }
        };

        window.addEventListener('focus', fetchUnreadCount);
        window.addEventListener(MESSAGE_STATUS_CHANGED_EVENT, fetchUnreadCount);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('focus', fetchUnreadCount);
            window.removeEventListener(MESSAGE_STATUS_CHANGED_EVENT, fetchUnreadCount);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [fetchUnreadCount]);

    if (companyId <= 0) {
        return null;
    }

    const tooltipText = unreadCount > 0
        ? `${unreadCount} unread message${unreadCount === 1 ? '' : 's'}`
        : 'No unread messages';

    return (
        <Tooltip title={tooltipText}>
            <IconButton
                color="inherit"
                aria-label={tooltipText}
                onClick={() => navigate('/Support/MessageCenter')}
                size="large"
            >
                <Badge badgeContent={unreadCount} color="error" max={99}>
                    {unreadCount > 0 ? <MailIcon /> : <MailOutlineIcon />}
                </Badge>
            </IconButton>
        </Tooltip>
    );
}
